import type { Resource } from '@medplum/fhirtypes';
import type { ReactNode } from 'react';

import { EM_DASH } from '../core/copy';
import { resourceKey } from '../core/resolution-map';

export type FhirTableColumn = {
  header: ReactNode;
  /** Projects any resource onto this column. Mixed types are expected — dispatch inside (or use the semantic accessors). */
  cell: (resource: Resource) => ReactNode;
  /** Stable column key when header isn't a string. */
  key?: string;
};

export type FhirTableProps = {
  resources: readonly Resource[];
  columns: readonly FhirTableColumn[];
  className?: string;
  /** Rendered when resources is empty. */
  empty?: ReactNode;
};

/**
 * Tabular collection view: a shared column projection over resources of any
 * mix of types. Bypasses the registry/walker on purpose — the columns are the
 * contract, so alignment holds regardless of entity type. Grid-based with
 * table roles for accessibility.
 */
export function FhirTable({
  resources,
  columns,
  className,
  empty = null,
}: FhirTableProps) {
  if (resources.length === 0) return empty;

  return (
    <div
      role="table"
      className={className ?? 'grid gap-x-4 gap-y-1 text-sm'}
      style={{ gridTemplateColumns: `repeat(${columns.length}, auto)` }}
    >
      <div role="row" className="contents">
        {columns.map((column, columnIndex) => (
          <span
            key={column.key ?? columnIndex}
            role="columnheader"
            className="font-medium text-muted-foreground"
          >
            {column.header}
          </span>
        ))}
      </div>
      {resources.map((resource, rowIndex) => (
        <div
          role="row"
          className="contents"
          key={resourceKey(resource) ?? rowIndex}
        >
          {columns.map((column, columnIndex) => (
            <span role="cell" key={column.key ?? columnIndex}>
              {column.cell(resource) ?? EM_DASH}
            </span>
          ))}
        </div>
      ))}
    </div>
  );
}
